import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { iconFile } from '@/lib/icon-raster';
import { ICON_RASTER_PX, findIconAsset, searchIcons, type CatalogIcon } from '@/lib/icons';
import type { Asset } from '@/lib/queries';
import { Loader2, Search } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';

// PICK A GLYPH, GET AN ASSET. The picker is the only place vector artwork exists in this product;
// what leaves it is a PNG asset id, which is all the rest of the system — canvas, preflight,
// preview, publish, four SDKs — ever sees.

export interface IconPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Every asset in the app. Searched for an existing mint BEFORE uploading: picking `check` twice
   *  must cost one asset, not two. */
  assets: readonly Asset[];
  /** Rasterize + upload. Returns the created asset. */
  onUpload: (file: File) => Promise<Asset>;
  /** The chosen glyph, resolved to a managed asset id. */
  onChoose: (assetId: string) => void;
}

export function IconPicker({
  open,
  onOpenChange,
  assets,
  onUpload,
  onChoose,
}: IconPickerProps): React.JSX.Element {
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const grid = useRef<HTMLDivElement>(null);
  const results = useMemo(() => searchIcons(query), [query]);

  async function choose(icon: CatalogIcon): Promise<void> {
    if (busy) return;
    setError(null);

    // ALREADY MINTED? Then this pick is free and instant — no canvas, no upload, no second copy of
    // the same glyph in the author's library.
    const existing = findIconAsset(assets, icon.name);
    if (existing) {
      onChoose(existing.id);
      onOpenChange(false);
      return;
    }

    // The LIVE node the author just clicked is the thing we rasterize — the picker already renders
    // every icon, so there is no second source of truth to keep in step with the preview.
    const svg = grid.current?.querySelector<SVGSVGElement>(`[data-icon="${icon.name}"] svg`);
    if (!svg) {
      setError('That icon could not be read. Try another.');
      return;
    }

    setBusy(icon.name);
    try {
      const asset = await onUpload(await iconFile(svg, icon.name, ICON_RASTER_PX));
      onChoose(asset.id);
      onOpenChange(false);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'That icon could not be added.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add an icon</DialogTitle>
          <DialogDescription>
            Icons are added to your assets as {ICON_RASTER_PX}px images and coloured with a theme
            token, so one glyph works on light and dark.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search
            className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search icons"
            aria-label="Search icons"
            className="pl-8"
          />
        </div>

        {error ? (
          <p role="alert" className="text-xs text-destructive">
            {error}
          </p>
        ) : null}

        <div
          ref={grid}
          role="listbox"
          aria-label="Icons"
          className="grid max-h-80 grid-cols-4 gap-1 overflow-y-auto sm:grid-cols-6"
        >
          {results.map((icon) => {
            const minted = findIconAsset(assets, icon.name) !== undefined;
            const loading = busy === icon.name;
            return (
              <button
                key={icon.name}
                type="button"
                role="option"
                aria-selected={false}
                data-icon={icon.name}
                disabled={busy !== null}
                title={minted ? `${icon.label} — already in your assets` : icon.label}
                onClick={() => void choose(icon)}
                className="flex flex-col items-center gap-1 rounded-md border border-transparent px-1 py-3 text-[10px] leading-tight text-muted-foreground transition-colors hover:border-border hover:bg-secondary/60 hover:text-foreground disabled:opacity-40"
              >
                {loading ? (
                  <Loader2 className="size-5 animate-spin" aria-hidden="true" />
                ) : (
                  <icon.Icon className="size-5" aria-hidden="true" />
                )}
                <span className="w-full truncate">{icon.label}</span>
              </button>
            );
          })}
          {results.length === 0 ? (
            <p className="col-span-full px-1 py-6 text-center text-xs text-muted-foreground">
              No icon matches “{query}”. Upload your own from the Assets panel — any PNG on a
              transparent background works as an icon.
            </p>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
